import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, enc, type LotSummary } from '../api';
import { ErrorBox, Empty, Loading, LotKindBadge, Pager, Thumb, Val, fmtDate } from '../components/ui';
import { useDebounced, useLocations } from '../hooks';

export function LotsPage() {
  const nav = useNavigate();
  const locations = useLocations(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('available');
  const [loc, setLoc] = useState('');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const dq = useDebounced(q.trim());

  const params = new URLSearchParams({ status, page: String(page) });
  if (dq) params.set('q', dq);
  if (kind) params.set('kind', kind);
  if (loc === 'none') params.set('unlocated', '1');
  else if (loc) params.set('locationId', loc);

  const res = useQuery({
    queryKey: ['lots', params.toString()],
    queryFn: () => api<{ lots: LotSummary[]; total: number; page: number; pageSize: number }>(`/lots?${params}`),
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <div className="row between">
        <h1>Lots</h1>
        <Link className="btn btn-primary" to="/lots/new">
          New lot
        </Link>
      </div>
      <div className="filters">
        <input
          type="search"
          placeholder="Lot number, item number, title, colour…"
          value={q}
          onChange={(e) => (setQ(e.target.value), setPage(1))}
          onKeyDown={async (e) => {
            // A scanner presses Enter: jump straight to an exact label match.
            if (e.key === 'Enter' && q.trim()) {
              const r = await api<{ available: boolean; lot: { id: string } | null }>(`/lots/check-number/${enc(q.trim())}`).catch(() => null);
              if (r?.lot) nav(`/lots/${r.lot.id}`);
            }
          }}
        />
        <select value={status} onChange={(e) => (setStatus(e.target.value), setPage(1))} aria-label="Status">
          <option value="available">Available</option>
          <option value="void">Void</option>
          <option value="all">All</option>
        </select>
        <select value={loc} onChange={(e) => (setLoc(e.target.value), setPage(1))} aria-label="Location">
          <option value="">All locations</option>
          <option value="none">No location</option>
          {locations.data?.map((l) => (
            <option key={l.id} value={l.id}>
              {l.code}
            </option>
          ))}
        </select>
        <select value={kind} onChange={(e) => (setKind(e.target.value), setPage(1))} aria-label="Kind of lot">
          <option value="">All kinds</option>
          <option value="incomplete">Incomplete items</option>
          <option value="mixed">Mixed / parts lots</option>
        </select>
      </div>
      {res.isLoading && <Loading />}
      <ErrorBox error={res.error} onRetry={() => res.refetch()} />
      {res.data && res.data.lots.length === 0 && <Empty>No lots match.</Empty>}
      {res.data && res.data.lots.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th></th>
                <th>Label</th>
                <th>Title</th>
                <th>Item #</th>
                <th>Condition</th>
                <th>Colour</th>
                <th>Location</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {res.data.lots.map((l) => (
                <tr key={l.id} onClick={() => nav(`/lots/${l.id}`)} className="clickable">
                  <td>
                    <Thumb id={l.primaryPhotoId} size={44} />
                  </td>
                  <td className="mono">
                    <Link to={`/lots/${l.id}`}>{l.lotNumber}</Link>
                    {l.status === 'void' && <span className="badge badge-bad">void</span>}
                  </td>
                  <td>
                    <Val v={l.title} missing="no title" />
                    <LotKindBadge lot={l} />
                  </td>
                  <td className="mono">{l.productItemNumber ?? 'MIXED'}</td>
                  <td>
                    <Val v={l.conditionLabel} missing="—" />
                  </td>
                  <td>
                    <Val v={l.colour} missing="—" />
                  </td>
                  <td>
                    <Val v={l.locationCode} missing="none" />
                    {l.binCode && <div className="small muted">🧺 {l.binCode}</div>}
                  </td>
                  <td className="small">{fmtDate(l.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {res.data && <Pager page={res.data.page} pageSize={res.data.pageSize} total={res.data.total} onPage={setPage} />}
    </div>
  );
}
